import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { PORTAL_API_HOST } from './config';

const TOKEN_STORAGE_KEY = 'user';

const readToken = (): string | null => {
    try {
        const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { idToken?: string };
        return parsed.idToken ?? null;
    } catch {
        return null;
    }
};

const buildClient = (): AxiosInstance => {
    const client = axios.create({
        baseURL: PORTAL_API_HOST,
        timeout: 30_000,
    });

    client.interceptors.request.use((cfg) => {
        const token = readToken();
        if (token) {
            cfg.headers = cfg.headers ?? {};
            (cfg.headers as Record<string, string>).Authorization = `Bearer ${token}`;
        }
        return cfg;
    });

    return client;
};

export const apiClient = buildClient();

export interface AuthInterceptorHandlers {
    refreshToken: () => Promise<string | null>;
    onAuthFailed: () => void;
}

let installedResponseInterceptorId: number | null = null;

export const installAuthInterceptor = ({
    refreshToken,
    onAuthFailed,
}: AuthInterceptorHandlers): (() => void) => {
    if (installedResponseInterceptorId !== null) {
        apiClient.interceptors.response.eject(installedResponseInterceptorId);
        installedResponseInterceptorId = null;
    }

    const id = apiClient.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error?.config as
                | (AxiosRequestConfig & { _retry?: boolean })
                | undefined;
            const status = error?.response?.status;

            if (status === 401 && originalRequest && !originalRequest._retry) {
                originalRequest._retry = true;
                const newToken = await refreshToken();
                if (newToken) {
                    originalRequest.headers = originalRequest.headers ?? {};
                    (originalRequest.headers as Record<string, string>).Authorization =
                        `Bearer ${newToken}`;
                    try {
                        return await apiClient.request(originalRequest);
                    } catch (retryError) {
                        onAuthFailed();
                        return Promise.reject(retryError);
                    }
                }
                onAuthFailed();
            }
            return Promise.reject(error);
        },
    );

    installedResponseInterceptorId = id;

    return () => {
        if (installedResponseInterceptorId === id) {
            apiClient.interceptors.response.eject(id);
            installedResponseInterceptorId = null;
        }
    };
};

export const get = <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config);

export const post = <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config);

export const put = <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config);

export const patch = <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config);

export const del = <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config);
